// Web product analytics in TelemetryDeck — the same app the iOS SDK reports
// to, so one dashboard shows iOS and web side by side.
//
// Same rules as iOS Core/Analytics.swift (COPPA): a signal is a static
// snake_case name plus closed-vocabulary details — short lowercase slugs
// (surface, destination, first/again). Never titles, names, prompts or ids;
// anything that isn't a slug is dropped here rather than sent.
//
// Identity matches iOS so a family is one user across both: the lowercased
// Supabase user id, SHA-256 hashed in the browser before it leaves. Signed-out
// visitors get a random per-browser id. Off pixleyai.com every signal is Test
// Mode, like iOS DEBUG/TestFlight builds, so local and preview runs never count.

import { useEffect } from "react";

const APP_ID = "743BE1C9-6A7F-47B6-ABCA-CA5803A2C707";
const INGEST_URL = "https://nom.telemetrydeck.com/v2/";
const SLUG = /^[a-z0-9_]{1,40}$/;
const ANON_KEY = "pixley_td_anon_id";
const SESSION_KEY = "pixley_td_session_id";
const SHARED_KEY = "pixley_td_shared";

let accountId = null;

/** AuthContext calls this whenever the signed-in user changes. */
export function setTelemetryUser(userId) {
  accountId = userId ? String(userId).trim().toLowerCase() : null;
}

export function isSignedIn() {
  return Boolean(accountId);
}

function randomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function stored(storage, key) {
  try {
    let value = storage.getItem(key);
    if (!value) {
      value = randomId();
      storage.setItem(key, value);
    }
    return value;
  } catch {
    return randomId();
  }
}

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function isTestMode() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return !(host === "pixleyai.com" || host === "www.pixleyai.com" || host === "make.pixleyai.com");
}

/** Fire-and-forget. Never throws, never blocks the caller. */
export function tdSignal(type, details = {}) {
  if (!SLUG.test(type || "")) return;
  if (typeof window === "undefined" || !window.crypto?.subtle) return;
  if (process.env.NODE_ENV === "test") return;
  const payload = { "TelemetryDeck.Device.platform": "Web", platform: "Web" };
  Object.entries(details).forEach(([key, value]) => {
    if (value != null && SLUG.test(String(value))) payload[key] = String(value);
  });
  const user = accountId || stored(localStorage, ANON_KEY).toLowerCase();
  sha256Hex(user)
    .then((clientUser) => fetch(INGEST_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify([{
        appID: APP_ID,
        clientUser,
        sessionID: stored(sessionStorage, SESSION_KEY),
        type,
        payload,
        isTestMode: isTestMode() ? "true" : "false",
      }]),
    }))
    .catch(() => {});
}

// ── Sharing (dashboard "9 · Sharing") ───────────────────────────────────────
// surface: story (link) | video (the file / social composer) | storybook |
//          character | game
// destination: copy_link | instagram | facebook | x | save | other

/** first = this browser hasn't shared this item before; again = a reshare.
 *  The ids stay in this browser; only first/again is sent. */
function shareRepeat(surface, contentId) {
  if (!contentId) return undefined;
  const key = `${surface}:${contentId}`;
  try {
    const seen = JSON.parse(localStorage.getItem(SHARED_KEY) || "[]");
    if (seen.includes(key)) return "again";
    localStorage.setItem(SHARED_KEY, JSON.stringify([...seen, key].slice(-500)));
    return "first";
  } catch {
    return undefined;
  }
}

export const trackShare = {
  /** The share sheet / modal opened. */
  tapped: (surface) => tdSignal("share_tapped", { surface }),
  /** The family sent it somewhere (or copied / saved it). */
  completed: (surface, destination, contentId) =>
    tdSignal("share_completed", {
      surface,
      destination,
      story_share: shareRepeat(surface, contentId),
    }),
  /** The sheet closed without sharing. */
  cancelled: (surface) => tdSignal("share_cancelled", { surface }),
};

/** Someone opened a shared link (story / storybook / character / game). */
export function trackShareLinkOpened(surface) {
  tdSignal("share_link_opened", { surface, viewer: accountId ? "signed_in" : "guest" });
}

/** Report a shared-link visit once per item per page view. Waits a moment so
 *  a signed-in visitor's session has loaded (else they'd count as a guest). */
export function useShareLinkOpened(surface, key, active = true) {
  useEffect(() => {
    if (!active || !key) return undefined;
    const timer = setTimeout(() => trackShareLinkOpened(surface), 1500);
    return () => clearTimeout(timer);
  }, [surface, key, active]);
}
